class ProductCarousel extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.cards = [];
    this.isAnimating = false;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.prevBtn = this.querySelector('.product-carousel__arrow--prev');
    this.nextBtn = this.querySelector('.product-carousel__arrow--next');
    this.live = this.querySelector('.product-carousel__live');

    if (this.dataset.layout === 'row') {
      this.initRow();
      return;
    }

    const source = this.dataset.source;
    if (source === 'related' || source === 'recommendations') {
      this.hydrateFromApi();
    } else {
      this.hydrateFromDom();
    }
  }

  initRow() {
    const track = this.track;
    if (!track || !this.prevBtn || !this.nextBtn) return;
    const step = () => {
      const card = track.querySelector('[data-product-card]');
      return card ? card.getBoundingClientRect().width + 16 : 300;
    };
    // Scroll by the number of fully visible cards (>= 1) so the snap
    // points line up and no card is skipped on narrow viewports.
    const page = () => {
      const s = step();
      return Math.max(1, Math.floor(track.clientWidth / s)) * s;
    };
    this.prevBtn.addEventListener('click', () => track.scrollBy({ left: -page(), behavior: 'smooth' }));
    this.nextBtn.addEventListener('click', () => track.scrollBy({ left: page(), behavior: 'smooth' }));
    const update = () => {
      this.prevBtn.disabled = track.scrollLeft <= 2;
      this.nextBtn.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    };
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  hydrateFromDom() {
    const els = Array.from(this.track.querySelectorAll('[data-product-card]'));
    if (els.length === 0) return;
    this.cards = els.map((el) => ({
      el,
      title: el.querySelector('.pcs__title')?.textContent ?? '',
    }));
    this.render();
    this.attachEvents();
    this.maybeStartAutoplay();
  }

  async hydrateFromApi() {
    const url = `${this.dataset.recommendationsUrl}?product_id=${this.dataset.productId}&intent=${this.dataset.intent}&limit=${this.dataset.max}&section_id=product-recommendations`;
    try {
      const res = await fetch(url);
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const items = Array.from(doc.querySelectorAll('[data-product-card]'));
      items.forEach((el) => {
        el.classList.add('product-carousel__slot');
        this.track.appendChild(el);
      });
      const els = Array.from(this.track.querySelectorAll('[data-product-card]'));
      if (els.length < 3) {
        this.style.display = 'none';
        return;
      }
      this.cards = els.map((el) => ({
        el,
        title: el.querySelector('.pcs__title')?.textContent ?? '',
      }));
      this.render();
      this.attachEvents();
      this.maybeStartAutoplay();
    } catch (err) {
      console.error('[product-carousel] hydrate failed', err);
    }
  }

  computePosition(cardIndex) {
    const n = this.cards.length;
    let d = cardIndex - this.activeIndex;
    const half = n / 2;
    if (d > half) d -= n;
    if (d <= -half) d += n;
    return d;
  }

  render() {
    const n = this.cards.length;
    this.cards.forEach((card, i) => {
      const pos = this.computePosition(i);
      if (pos >= -2 && pos <= 2) {
        card.el.dataset.position = String(pos);
        card.el.setAttribute('aria-hidden', pos === 0 ? 'false' : 'true');
      } else {
        card.el.dataset.position = 'hidden';
        card.el.setAttribute('aria-hidden', 'true');
      }
    });

    // Always wrap — even with 3 cards, the visual layout already shows a
    // wrapped arrangement (one card on each side of the center), so arrow
    // buttons and side-card clicks should navigate in both directions.
    this.prevBtn.disabled = false;
    this.nextBtn.disabled = false;

    const title = this.cards[this.activeIndex]?.title;
    if (title && this.live) this.live.textContent = title;
  }

  step(delta) {
    if (this.isAnimating || this.cards.length === 0) return;
    const n = this.cards.length;
    this.activeIndex = (((this.activeIndex + delta) % n) + n) % n;
    this.isAnimating = true;
    this.render();
    const centerEl = this.cards[this.activeIndex]?.el;
    if (centerEl) {
      centerEl.addEventListener('transitionend', () => { this.isAnimating = false; }, { once: true });
    } else {
      this.isAnimating = false;
    }
  }

  attachEvents() {
    this.prevBtn.addEventListener('click', () => this.step(-1));
    this.nextBtn.addEventListener('click', () => this.step(1));

    this.cards.forEach((card) => {
      card.el.addEventListener('click', (e) => {
        const pos = Number(card.el.dataset.position);
        if (pos === 0 || Number.isNaN(pos)) return;
        e.preventDefault();
        this.step(pos);
      });
    });

    let startX = null;
    this.track.addEventListener('pointerdown', (e) => { startX = e.clientX; });
    this.track.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 50) this.step(dx < 0 ? 1 : -1);
      startX = null;
    });
    this.track.addEventListener('pointercancel', () => { startX = null; });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { this.step(-1); e.preventDefault(); }
      if (e.key === 'ArrowRight') { this.step(1); e.preventDefault(); }
    });
    this.setAttribute('tabindex', '0');
  }

  maybeStartAutoplay() {
    if (this.dataset.autoplay !== 'true') return;
    const interval = Number(this.dataset.autoplayInterval) || 5000;
    this.autoplayInterval = interval;
    this.startAutoplay();

    this.addEventListener('pointerenter', () => this.stopAutoplay());
    this.addEventListener('pointerleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    this._visibilityHandler = () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    };
    document.addEventListener('visibilitychange', this._visibilityHandler);
  }

  startAutoplay() {
    if (!this.autoplayInterval || this.autoplayTimer) return;
    this.autoplayTimer = setInterval(() => this.step(1), this.autoplayInterval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
  }
}

customElements.define('product-carousel', ProductCarousel);
